/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  CRUDPermissions,
  GenericObject,
  Group
} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {useHasInheritable} from '@ion/dls/src/hooks/useHasInheritable';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import {IconSize} from '@ion/dls/src/types/types';
import {globalGroupSelector} from '../../../globalsearch/selector';
import * as actions from './state';

// defines the group row editor prop definitions ...
export interface GroupRowEditorTypes {
  group: Group;
  includeGlobalSearch?: boolean;
  groupsPermissions?: CRUDPermissions;
  hasInheritableGroups?: boolean;
  ancestorsGroups?: GenericObject;
}

/**
 * Defines the GroupRowEditor component. This component contains the cell actions
 * for Groups grid.
 */
export const GroupRowEditor = ({
  group,
  groupsPermissions,
  ancestorsGroups,
  includeGlobalSearch = false,
  hasInheritableGroups = false
}: GroupRowEditorTypes) => {
  const {
    account,
    inheritable
  } = group || {};

  const {
    hasReadPermission,
    hasUpdatePermission,
    hasDeletePermission
  } = groupsPermissions || {};

  const dispatch        = useDispatch();
  const cellMenuRef     = useRef(null);
  const {globalGroup}   = useSelector(globalGroupSelector) || {};
  const globalAccountId = entityId(globalGroup);
  const groupAccountId  = entityId(account);

  const hasInheritable = useHasInheritable({
    ancestorsGroups,
    groupAccountId,
    globalAccountId,
    inheritable
  });

  function closeMenu() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const onAction = function(type: string) {
    return function() {
      dispatch(actions[type](group));
      closeMenu();
    };
  };

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div>
          <CellMenuItem icon={<ViewRecordIcon size={IconSize.medium} />}
            onClick={onAction('viewGroup')}
            disabled={!hasReadPermission}
            label={translate('groups:grid.menu.view')} />
          <CellMenuItem icon={<MapEditIcon />}
            onClick={onAction('editGroup')}
            disabled={hasInheritableGroups && (!hasUpdatePermission || hasInheritable)}
            label={translate('groups:grid.menu.edit')} />
          <CellMenuItem icon={<NotificationTimeIcon />}
            onClick={onAction('groupAudit')}
            label={translate('groups:grid.menu.auditLog')} />
          {!includeGlobalSearch && (
            <CellMenuItem icon={<SystemDeleteIcon />}
              onClick={onAction('deleteAdminGroup')}
              disabled={hasInheritableGroups && (!hasDeletePermission || hasInheritable)}
              label={translate('groups:grid.menu.delete')} />
          )}
        </div>
      } />
    </>
  );
};
